import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Pin, Clock } from 'lucide-react';
import {
  motion,
  AnimatePresence,
  useDragControls,
  useAnimationControls,
  PanInfo,
} from 'framer-motion';
import { UserEvent, CreateEventRequest, ToastType, CustomList } from '../../types/types';
import { createUserEvent, updateUserEvent } from '../../lib/eventApi';
import { auth } from '../../lib/firebase';

// ─── Controlled vocabularies ─────────────────────────────────────────────────

// These match the 3 filter presets in BottomSheet (PRESET_LABELS).
// label = what the user sees; value = what gets stored in the backend.
const CATEGORY_OPTIONS: { label: string; value: string }[] = [
  { label: 'movement', value: 'active' },
  { label: 'music', value: 'music' },
  { label: 'culture', value: 'event' },
];
const DAY_ABBREVS: [string, number][] = [
  ['Mo', 1],
  ['Tu', 2],
  ['We', 3],
  ['Th', 4],
  ['Fr', 5],
  ['Sa', 6],
  ['Su', 7],
];

const REGION_TIMEZONES: Record<string, string> = {
  bay_area: 'America/Los_Angeles',
  utah: 'America/Denver',
  goa: 'Asia/Kolkata',
  japan: 'Asia/Tokyo',
  east_coast: 'America/New_York',
  park_city: 'America/Denver',
  oakland: 'America/Los_Angeles',
  nyc: 'America/New_York',
};

const TOTAL_STEPS = 5;

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventSubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  coordinates: [number, number] | null; // [lng, lat]
  onSuccess: (event: UserEvent, selectedListId?: number | null) => void;
  showToast: (message: string, type?: ToastType, duration?: number, dismissible?: boolean) => void;
  isMobile: boolean;
  initialData?: UserEvent; // present when editing
  selectedCityRegion?: string; // new — replaces region dropdown
  userLists?: CustomList[]; // for optional list association in step 2
  onAuthRequired?: () => void; // called when unauthenticated user tries to save
  onDiscard?: () => void; // called on confirmed discard (triggers marker pop in parent)
}

interface FormData {
  name: string;
  category: string;
  selectedListId: number | null;
  start_date: string;
  expiry: string;
  description: string;
  repeat: string;
  interval?: number; // INTERVAL: every N units (default 1); used with weekly for biweekly etc.
  days: number[];
  bysetpos?: number[]; // BYSETPOS: ordinal positions within the period; used with monthly
  start_time: string;
  end_time: string;
  circular_text: string;
}

const DEFAULT_FORM: FormData = {
  name: '',
  category: '',
  selectedListId: null,
  start_date: '',
  expiry: '',
  description: '',
  repeat: 'once',
  days: [],
  start_time: '',
  end_time: '',
  circular_text: '',
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 10000,
  backgroundColor: 'rgba(0,0,0,0.3)',
};

const MODAL_STYLE: React.CSSProperties = {
  position: 'fixed',
  zIndex: 10002,
  bottom: 0,
  left: 0,
  right: 0,
  maxHeight: '88dvh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--color-surface)',
  borderRadius: '20px 20px 0 0',
  boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
};

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-content-muted)',
  marginBottom: '6px',
};

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1.5px solid var(--color-border)',
  fontSize: '15px',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-surface)',
};

const PILL_STYLE = (active: boolean): React.CSSProperties => ({
  padding: '6px 14px',
  borderRadius: '20px',
  border: active ? '2px solid var(--color-primary)' : '1.5px solid var(--color-border)',
  backgroundColor: active ? 'rgba(65, 100, 81,0.12)' : 'var(--color-surface)',
  color: active ? 'var(--color-primary)' : 'var(--color-content-muted)',
  fontSize: '13px',
  cursor: 'pointer',
  fontWeight: active ? 600 : 400,
  boxShadow: 'var(--shadow-sm)',
  transition: 'all 0.15s',
});

const BTN_PRIMARY: React.CSSProperties = {
  padding: '12px 24px',
  borderRadius: '12px',
  border: 'none',
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-content-inverse)',
  fontSize: '15px',
  fontWeight: 600,
  cursor: 'pointer',
};

// ─── Component ────────────────────────────────────────────────────────────────

const EventSubmitModal: React.FC<EventSubmitModalProps> = ({
  isOpen,
  onClose,
  coordinates,
  onSuccess,
  showToast: _showToast,
  isMobile: _isMobile,
  initialData,
  selectedCityRegion,
  userLists = [],
  onAuthRequired,
  onDiscard,
}) => {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [_isDirty, setIsDirty] = useState(false);
  const [modalState, setModalState] = useState<'expanded' | 'peek'>('expanded');
  const [showDiscardPortal, setShowDiscardPortal] = useState(false);
  const [peekOffset, setPeekOffset] = useState(0);

  // Custom recurrence dialog state
  const [showCustomRecurrence, setShowCustomRecurrence] = useState(false);
  const [customRepeatEvery, setCustomRepeatEvery] = useState(1);
  const [customRepeatUnit, setCustomRepeatUnit] = useState<'day' | 'week' | 'month' | 'year'>(
    'week'
  );
  const [customRepeatDays, setCustomRepeatDays] = useState<number[]>([]);
  const [customEndsType, setCustomEndsType] = useState<'never' | 'on'>('never');
  const [customEndsDate, setCustomEndsDate] = useState('');

  const sheetRef = useRef<HTMLDivElement>(null);
  const dragControls = useDragControls();
  const sheetControls = useAnimationControls();
  const stepRef = useRef(step);
  stepRef.current = step;

  // Pre-fill when editing
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        const meta = initialData.metadata || {};
        const sched = initialData.schedule || {};
        setFormData({
          name: initialData.name || '',
          category: initialData.category || '',
          selectedListId: null,
          start_date: initialData.start_date || '',
          expiry: initialData.expiry || '',
          description: (meta.description as string) || '',
          repeat: sched.repeat || 'once',
          interval: sched.interval ?? undefined,
          days: sched.days || [],
          bysetpos: sched.bysetpos ?? undefined,
          start_time: sched.start_time || '',
          end_time: sched.end_time || '',
          circular_text: (meta.circular_text as string) || '',
        });
      } else {
        setFormData(DEFAULT_FORM);
      }
      setStep(1);
      setDirection(1);
      setCalendarMonth(new Date());
      setError(null);
      setNameError(null);
      setDateError(null);
      setIsDirty(false);
      setModalState('expanded');
      setShowDiscardPortal(false);
      setShowCustomRecurrence(false);
      setCustomRepeatEvery(1);
      setCustomRepeatUnit('week');
      setCustomRepeatDays([]);
      setCustomEndsType('never');
      setCustomEndsDate('');
    }
  }, [isOpen, initialData]);

  // Measure sheet height so peek offset is always correct even as content changes
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;
    const update = () => {
      if (sheetRef.current) setPeekOffset(sheetRef.current.offsetHeight - 64);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(sheetRef.current);
    return () => observer.disconnect();
  }, [isOpen]);

  // Slide sheet in when it opens, and keep sheetControls in sync with modalState
  useEffect(() => {
    if (isOpen) {
      sheetControls.start({ y: 0, transition: { duration: 0.28, ease: [0.32, 0.72, 0, 1] } });
    }
  }, [isOpen, sheetControls]);

  useEffect(() => {
    if (!isOpen) return;
    sheetControls.start({
      y: modalState === 'peek' ? peekOffset : 0,
      transition: { duration: 0.28, ease: [0.32, 0.72, 0, 1] },
    });
  }, [modalState, peekOffset, isOpen, sheetControls]);

  if (!isOpen) return null;

  const isEditing = Boolean(initialData);
  const isPendingReview = initialData?.visibility === 'pending_review'; // read-only display only

  const set = (key: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const goNext = () => {
    setDirection(1);
    setStep(s => s + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setStep(s => s - 1);
    setError(null);
    setNameError(null);
    setDateError(null);
  };

  const handleNext = () => {
    if (step >= TOTAL_STEPS) return;
    if (step === 1) {
      if (!formData.name.trim()) {
        setNameError('Event name is required');
        return;
      }
      setNameError(null);
    }
    if (step === 3) {
      if (formData.start_date && formData.expiry && formData.start_date > formData.expiry) {
        setDateError('Start date must be on or before end date');
        return;
      }
      setDateError(null);
    }
    goNext();
  };

  // Auto-select day when repeat === 'once'; clear interval + bysetpos for non-custom repeats
  const handleRepeatChange = (val: string) => {
    // JS getDay(): 0=Sun, 1=Mon…6=Sat → backend: 1=Mon…7=Sun
    const backendDayFromStartDate = (): number | null => {
      if (!formData.start_date) return null;
      const jsDay = new Date(formData.start_date + 'T00:00:00').getDay();
      return jsDay === 0 ? 7 : jsDay;
    };

    if (val === 'daily') {
      // Daily events occur every day — all 7 weekdays required for the expansion gate
      setFormData(prev => ({
        ...prev,
        repeat: val,
        days: [1, 2, 3, 4, 5, 6, 7],
        interval: undefined,
        bysetpos: undefined,
      }));
    } else if ((val === 'once' || val === 'weekly' || val === 'annual') && formData.start_date) {
      const backendDay = backendDayFromStartDate()!;
      setFormData(prev => ({
        ...prev,
        repeat: val,
        days: [backendDay],
        interval: undefined,
        bysetpos: undefined,
      }));
    } else {
      setFormData(prev => ({ ...prev, repeat: val, interval: undefined, bysetpos: undefined }));
    }
    setIsDirty(true);
  };

  // Maps formData.repeat to the repeat <select> option value
  const getDropdownRepeatValue = (): string => {
    switch (formData.repeat) {
      case 'once':
        return 'once';
      case 'daily':
        return 'daily';
      // weekly with interval > 1 (biweekly, every 3 weeks) shows as "Custom…"
      case 'weekly':
        return (formData.interval ?? 1) > 1 ? 'custom' : 'weekly';
      case 'monthly':
        return 'monthly';
      case 'annual':
        return 'annual';
      default:
        return 'custom'; // 'alternating weeks' (legacy) or unknown
    }
  };

  const handleRepeatDropdownChange = (val: string) => {
    if (val === 'custom') {
      // Pre-fill custom dialog from current formData
      if (formData.repeat === 'alternating weeks') {
        setCustomRepeatEvery(2);
        setCustomRepeatUnit('week');
      } else if (formData.repeat === 'weekly' && (formData.interval ?? 1) > 1) {
        setCustomRepeatEvery(formData.interval!);
        setCustomRepeatUnit('week');
      } else if (formData.repeat === 'daily') {
        setCustomRepeatEvery(1);
        setCustomRepeatUnit('day');
      } else if (formData.repeat === 'monthly') {
        setCustomRepeatEvery(1);
        setCustomRepeatUnit('month');
      } else if (formData.repeat === 'annual') {
        setCustomRepeatEvery(1);
        setCustomRepeatUnit('year');
      } else {
        setCustomRepeatEvery(1);
        setCustomRepeatUnit('week');
      }
      if (formData.days.length > 0) {
        setCustomRepeatDays([...formData.days]);
      } else if (formData.start_date) {
        const jsDay = new Date(formData.start_date + 'T00:00:00').getDay();
        setCustomRepeatDays([jsDay === 0 ? 7 : jsDay]);
      } else {
        setCustomRepeatDays([]);
      }
      setCustomEndsType('never');
      setCustomEndsDate('');
      setShowCustomRecurrence(true);
      return;
    }
    if (val === 'monthly' && formData.start_date) {
      const d = new Date(formData.start_date + 'T00:00:00');
      const jsDay = d.getDay();
      const backendDay = jsDay === 0 ? 7 : jsDay;
      const ord = Math.min(5, Math.ceil(d.getDate() / 7));
      setFormData(prev => ({
        ...prev,
        repeat: 'monthly',
        bysetpos: [ord],
        interval: undefined,
        days: [backendDay],
      }));
      setIsDirty(true);
      return;
    }
    handleRepeatChange(val);
  };

  const toggleCustomDay = (dayInt: number) => {
    setCustomRepeatDays(prev =>
      prev.includes(dayInt) ? prev.filter(d => d !== dayInt) : [...prev, dayInt]
    );
  };

  const handleCustomRecurrenceDone = () => {
    let repeat: string;
    let interval: number | undefined;
    let days: number[] = customRepeatDays.length > 0 ? [...customRepeatDays] : [...formData.days];
    if (customRepeatUnit === 'day') {
      repeat = 'daily';
      days = [];
    } else if (customRepeatUnit === 'week') {
      repeat = 'weekly';
      interval = customRepeatEvery > 1 ? customRepeatEvery : undefined;
    } else if (customRepeatUnit === 'month') {
      repeat = 'monthly';
    } else {
      repeat = 'annual';
      days = [];
    }
    const update: Partial<FormData> = { repeat, days, interval, bysetpos: undefined };
    if (customEndsType === 'on' && customEndsDate) {
      update.expiry = customEndsDate;
    }
    setFormData(prev => ({ ...prev, ...update }));
    setIsDirty(true);
    setShowCustomRecurrence(false);
  };

  const deriveRegion = (): string => {
    if (selectedCityRegion) return selectedCityRegion;
    // Fallback: derive from browser timezone
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const entry = Object.entries(REGION_TIMEZONES).find(([, v]) => v === tz);
    return entry ? entry[0] : 'bay_area';
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!auth.currentUser) {
      onAuthRequired?.();
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const region = deriveRegion();
      const payload: CreateEventRequest = {
        name: formData.name,
        category: formData.category || 'event', // default when skipped
        start_date: formData.start_date,
        expiry: formData.expiry || formData.start_date, // single-tap = single-day event
        schedule: {
          days: formData.days,
          repeat: formData.repeat,
          start_time: formData.start_time,
          end_time: formData.end_time,
          ...(formData.interval !== undefined &&
            formData.interval > 1 && { interval: formData.interval }),
          ...(formData.bysetpos?.length && { bysetpos: formData.bysetpos }),
        },
        metadata: {
          timezone: REGION_TIMEZONES[region] ?? 'America/Los_Angeles',
          region,
          description: formData.description || undefined,
          circular_text: formData.circular_text || undefined,
        },
        longitude: coordinates![0],
        latitude: coordinates![1],
      };
      const event = isEditing
        ? await updateUserEvent(initialData!.id, payload)
        : await createUserEvent(payload);
      setIsSubmitting(false);
      onSuccess(event, formData.selectedListId);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleSheetDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const { velocity, offset } = info;
    // Single downward swipe from any state → discard portal (modal springs back via elastic)
    if (velocity.y > 300 || offset.y > 80) {
      if (!isSubmitting) setShowDiscardPortal(true);
    }
    // Upward swipe → ensure we're back to expanded (e.g. if peek was set via another path)
    else if (velocity.y < -200 || offset.y < -60) {
      setModalState('expanded');
    }
  };

  const handleCloseRequest = () => {
    if (!isSubmitting) {
      setShowDiscardPortal(true);
    } else {
      onClose();
    }
  };

  // Step-specific gate: returns true when the current step has its required fields filled
  const isNextEnabled = (): boolean => {
    if (step === 1) return formData.name.trim().length > 0;
    if (step === 3) return !!(formData.start_date && formData.start_time && formData.end_time);
    return true; // steps 2 and 4 are fully optional
  };

  // ── Month-grid calendar for step 3 ──────────────────────────────────────────

  const renderCalendar = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const monthName = calendarMonth.toLocaleString('default', { month: 'long' });
    const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startYMD = formData.start_date;
    const endYMD = formData.expiry;

    const handleDayTap = (day: number) => {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      if (!startYMD || (startYMD && endYMD)) {
        setFormData(prev => ({ ...prev, start_date: dateStr, expiry: '' }));
      } else if (dateStr < startYMD) {
        setFormData(prev => ({ ...prev, start_date: dateStr, expiry: prev.start_date }));
      } else {
        setFormData(prev => ({ ...prev, expiry: dateStr }));
      }
      setIsDirty(true);
      setDateError(null);
    };

    const cells: React.ReactNode[] = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      cells.push(<div key={`e${i}`} />);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isStart = dateStr === startYMD;
      const isEnd = dateStr === endYMD;
      const isBetween = !!(startYMD && endYMD && dateStr > startYMD && dateStr < endYMD);
      const isSelected = isStart || isEnd;
      cells.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDayTap(day)}
          aria-label={`Select ${dateStr}`}
          aria-pressed={isSelected}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '30px',
            cursor: 'pointer',
            border: 'none',
            padding: 0,
            backgroundColor: isBetween ? 'rgba(65, 100, 81,0.12)' : 'transparent',
          }}
        >
          <div
            style={{
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: isSelected ? 'var(--color-primary)' : 'transparent',
              color: isSelected
                ? 'var(--color-content-inverse)'
                : isBetween
                  ? 'var(--color-primary)'
                  : 'var(--color-content)',
              fontSize: '13px',
              fontWeight: isSelected ? 700 : isBetween ? 600 : 400,
            }}
          >
            {day}
          </div>
        </button>
      );
    }

    return (
      <div style={{ marginBottom: '12px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '6px',
          }}
        >
          <button
            type="button"
            onClick={() => setCalendarMonth(new Date(year, month - 1, 1))}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              fontSize: '16px',
              color: 'var(--color-content-muted)',
            }}
          >
            ◀
          </button>
          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-content)' }}>
            {monthName} {year}
          </span>
          <button
            type="button"
            onClick={() => setCalendarMonth(new Date(year, month + 1, 1))}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              fontSize: '16px',
              color: 'var(--color-content-muted)',
            }}
          >
            ▶
          </button>
        </div>
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' }}
        >
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <div
              key={i}
              style={{
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--color-content-muted)',
                padding: '2px 0',
              }}
            >
              {d}
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>{cells}</div>
      </div>
    );
  };

  // ── Render card content per step ────────────────────────────────────────────

  const renderStepContent = () => {
    switch (step) {
      // Step 1: Name
      case 1:
        return (
          <div>
            <p
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: 'var(--color-content)',
                marginBottom: '20px',
                lineHeight: 1.3,
              }}
            >
              What&apos;s the name of your event?
            </p>
            <input
              type="text"
              value={formData.name}
              onChange={e => {
                set('name', e.target.value);
                if (nameError) setNameError(null);
              }}
              placeholder="e.g. Sunday market"
              style={INPUT_STYLE}
              autoFocus
              data-testid="name-input"
            />
            {nameError && (
              <p style={{ color: 'var(--color-error)', fontSize: '12px', marginTop: '6px' }}>
                {nameError}
              </p>
            )}
          </div>
        );

      // Step 2: Category + optional list association
      case 2:
        return (
          <div>
            <p
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: 'var(--color-content)',
                marginBottom: '6px',
                lineHeight: 1.3,
              }}
            >
              Where does it fit?
            </p>
            <p
              style={{
                fontSize: '13px',
                color: 'var(--color-content-muted)',
                marginBottom: '18px',
              }}
            >
              Optional — pick a filter category, one of your lists, or skip
            </p>

            {/* Preset filter categories */}
            <p
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--color-content-muted)',
                marginBottom: '8px',
              }}
            >
              Filter category
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
              {CATEGORY_OPTIONS.map(({ label, value }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => set('category', formData.category === value ? '' : value)}
                  style={PILL_STYLE(formData.category === value)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* User's own lists */}
            {userLists.length > 0 && (
              <>
                <p
                  style={{
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: 'var(--color-content-muted)',
                    marginBottom: '8px',
                  }}
                >
                  Add to a list
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {userLists.map(list => (
                    <button
                      key={list.id}
                      type="button"
                      onClick={() =>
                        set('selectedListId', formData.selectedListId === list.id ? null : list.id)
                      }
                      style={PILL_STYLE(formData.selectedListId === list.id)}
                    >
                      {list.title || 'my list'}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        );

      // Step 3: Schedule — GCal-style, no scroll
      case 3: {
        const sd = formData.start_date;
        let weeklyLabel = 'Weekly';
        let monthlyLabel = 'Monthly';
        let annualLabel = 'Annually';
        if (sd) {
          const d = new Date(sd + 'T00:00:00');
          const jsDay = d.getDay();
          const dayNames = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
          ];
          const ordWords = ['', '1st', '2nd', '3rd', '4th', '5th'];
          const ord = Math.min(5, Math.ceil(d.getDate() / 7));
          weeklyLabel = `Weekly on ${dayNames[jsDay]}`;
          monthlyLabel = `Monthly on the ${ordWords[ord]} ${dayNames[jsDay]}`;
          annualLabel = `Annually on ${d.toLocaleString('default', { month: 'short', day: 'numeric' })}`;
        }
        return (
          <div>
            <p
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: 'var(--color-content)',
                lineHeight: 1.3,
                margin: 0,
                marginBottom: '16px',
              }}
            >
              When does it happen?
            </p>

            {renderCalendar()}

            {dateError && (
              <p style={{ color: 'var(--color-error)', fontSize: '12px', marginBottom: '12px' }}>
                {dateError}
              </p>
            )}

            {/* Repeat row */}
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}
            >
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--color-content-muted)',
                  flexShrink: 0,
                }}
              >
                Repeat
              </span>
              <select
                value={getDropdownRepeatValue()}
                onChange={e => handleRepeatDropdownChange(e.target.value)}
                data-testid="repeat-select"
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  borderRadius: '10px',
                  border: '1.5px solid var(--color-border)',
                  fontSize: '14px',
                  backgroundColor: 'var(--color-surface)',
                  color: 'var(--color-content)',
                  cursor: 'pointer',
                }}
              >
                <option value="once">Does not repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">{weeklyLabel}</option>
                <option value="monthly">{monthlyLabel}</option>
                <option value="annual">{annualLabel}</option>
                <option value="custom">Custom…</option>
              </select>
            </div>

            {/* Time row — side by side */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--color-content-muted)',
                  flexShrink: 0,
                }}
              >
                Time
              </span>
              <input
                type="time"
                value={formData.start_time}
                onChange={e => set('start_time', e.target.value)}
                data-testid="start-time-input"
                style={{ ...INPUT_STYLE, flex: 1, padding: '8px 10px' }}
              />
              <span
                style={{ color: 'var(--color-content-muted)', fontSize: '16px', flexShrink: 0 }}
              >
                –
              </span>
              <input
                type="time"
                value={formData.end_time}
                onChange={e => set('end_time', e.target.value)}
                data-testid="end-time-input"
                style={{ ...INPUT_STYLE, flex: 1, padding: '8px 10px' }}
              />
            </div>
          </div>
        );
      }

      // Step 4: Description (optional)
      case 4:
        return (
          <div>
            <p
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: 'var(--color-content)',
                marginBottom: '8px',
                lineHeight: 1.3,
              }}
            >
              Add a description
            </p>
            <p
              style={{
                fontSize: '13px',
                color: 'var(--color-content-muted)',
                marginBottom: '20px',
              }}
            >
              Optional — you can skip this
            </p>
            <label style={LABEL_STYLE}>
              Description
              <span
                style={{ fontWeight: 400, color: 'var(--color-content-muted)', marginLeft: '6px' }}
              >
                ({formData.description.length}/500)
              </span>
            </label>
            <textarea
              value={formData.description}
              onChange={e => {
                if (e.target.value.length <= 500) set('description', e.target.value);
              }}
              placeholder="What's this event about?"
              rows={4}
              style={{ ...INPUT_STYLE, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        );

      // Step 5: Circular text (optional) + confirm
      case 5:
        return (
          <div>
            <p
              style={{
                fontSize: '20px',
                fontWeight: 700,
                color: 'var(--color-content)',
                marginBottom: '8px',
                lineHeight: 1.3,
              }}
            >
              Add a marker label
            </p>
            <p
              style={{
                fontSize: '13px',
                color: 'var(--color-content-muted)',
                marginBottom: '20px',
              }}
            >
              Optional — add a label to spin around your marker
            </p>
            <input
              type="text"
              value={formData.circular_text}
              onChange={e => set('circular_text', e.target.value)}
              placeholder="e.g. every sunday • every sunday • "
              style={INPUT_STYLE}
              maxLength={60}
            />
            <p
              style={{
                fontSize: '11px',
                color: 'var(--color-content-muted)',
                marginTop: '6px',
                marginBottom: '20px',
              }}
            >
              {formData.circular_text.length}/60 characters
            </p>

            {/* Status indicator for pending_review edits */}
            {isPendingReview && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  backgroundColor: 'var(--color-surface-subtle)',
                  borderRadius: '10px',
                  marginBottom: '12px',
                }}
              >
                <Clock size={14} color="var(--color-forest-bright)" />
                <span style={{ fontSize: '13px', color: 'var(--color-content)', fontWeight: 600 }}>
                  pending admin review
                </span>
              </div>
            )}

            {/* Location confirmation */}
            {coordinates && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  backgroundColor: 'var(--color-surface-subtle)',
                  borderRadius: '10px',
                  marginBottom: '12px',
                }}
              >
                <Pin size={14} color="var(--color-content-muted)" />
                <span style={{ fontSize: '13px', color: 'var(--color-content-muted)' }}>
                  {isEditing
                    ? 'Swipe down to drag the pin to a new location'
                    : 'Location set from map'}
                </span>
              </div>
            )}

            {error && (
              <p style={{ color: 'var(--color-error)', fontSize: '13px', marginBottom: '12px' }}>
                {error}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = step === TOTAL_STEPS;

  const modal = (
    <AnimatePresence>
      {/* Backdrop — purely visual, never blocks map or marker interactions */}
      <motion.div
        key="backdrop"
        style={{ ...OVERLAY_STYLE, pointerEvents: 'none' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: modalState === 'peek' ? 0 : 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />

      {/* Sheet */}
      <motion.div
        key="sheet"
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-modal-title"
        style={MODAL_STYLE}
        onClick={e => e.stopPropagation()}
        initial={{ y: '100%' }}
        animate={sheetControls}
        exit={{ y: '100%' }}
        transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
        drag="y"
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0 }}
        dragElastic={{ top: 0.1, bottom: 0.3 }}
        onDragEnd={handleSheetDragEnd}
      >
        {/* Drag handle — initiates sheet drag; yellow bg matches header for seamless band */}
        <div
          role="button"
          tabIndex={0}
          aria-label="drag to dismiss"
          onPointerDown={e => dragControls.start(e)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleCloseRequest();
            }
          }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '6px 0 2px',
            touchAction: 'none',
            cursor: 'grab',
            backgroundColor: 'var(--color-yellow)',
            borderRadius: '20px 20px 0 0',
            minHeight: 'unset',
            minWidth: 'unset',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '4px',
              borderRadius: '2px',
              backgroundColor: 'rgba(200,200,200,0.8)',
            }}
          />
        </div>

        {/* Sticky header — also drags the sheet; shows event name once entered */}
        <div
          onPointerDown={e => dragControls.start(e)}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'var(--color-yellow)',
            padding: '8px 20px 8px',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            cursor: 'grab',
          }}
        >
          <h2
            id="event-modal-title"
            style={{
              fontSize: '16px',
              fontWeight: 700,
              color: 'var(--color-content)',
              margin: 0,
              textAlign: 'center',
              width: '100%',
            }}
          >
            {modalState === 'peek' && isEditing
              ? 'Drag the pin to reposition'
              : formData.name.trim() || (isEditing ? 'Edit event' : 'mark this spot')}
          </h2>
        </div>

        {/* Progress dots */}
        <div
          role="progressbar"
          aria-valuenow={step}
          aria-valuemin={1}
          aria-valuemax={TOTAL_STEPS}
          aria-label={`Step ${step} of ${TOTAL_STEPS}`}
          style={{
            display: 'flex',
            gap: '6px',
            padding: '10px 20px 6px',
            justifyContent: 'center',
          }}
        >
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              style={{
                width: i + 1 === step ? '20px' : '6px',
                height: '6px',
                borderRadius: '3px',
                backgroundColor: i + 1 <= step ? 'var(--color-primary)' : 'var(--color-border)',
                transition: 'all 0.25s ease',
              }}
            />
          ))}
        </div>

        {/* Card area with slide animation */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px 24px 0',
            position: 'relative',
          }}
        >
          <div style={{ maxWidth: '480px', margin: '0 auto' }}>
            <AnimatePresence initial={false} mode="popLayout">
              <motion.div
                key={step}
                custom={direction}
                variants={{
                  enter: (d: number) => ({ x: d * 60, opacity: 0 }),
                  center: { x: 0, opacity: 1 },
                  exit: (d: number) => ({ x: d * -60, opacity: 0 }),
                }}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.22, ease: 'easeInOut' }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.12}
                onDragEnd={(_, info) => {
                  if (info.offset.x < -60 && step < TOTAL_STEPS) handleNext();
                  else if (info.offset.x > 60 && step > 1) goBack();
                }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Navigation */}
        <div
          style={{
            padding: '16px 24px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--color-border)',
            gap: '12px',
          }}
        >
          {/* Back link */}
          {step > 1 ? (
            <button
              type="button"
              onClick={goBack}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: 'var(--color-content-muted)',
                padding: '8px 0',
              }}
            >
              ← Back
            </button>
          ) : (
            <div />
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Next / Submit button */}
            <div aria-live="polite">
              {isLastStep ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  style={{ ...BTN_PRIMARY, opacity: isSubmitting ? 0.7 : 1 }}
                >
                  {isSubmitting ? 'Saving…' : isEditing ? 'Update event' : 'Save event'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!isNextEnabled()}
                  style={{
                    ...BTN_PRIMARY,
                    opacity: isNextEnabled() ? 1 : 0.35,
                    cursor: isNextEnabled() ? 'pointer' : 'not-allowed',
                  }}
                >
                  Next →
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );

  const discardPortal = showDiscardPortal ? (
    <>
      {/* Discard portal backdrop */}
      <div
        role="button"
        tabIndex={0}
        aria-label="close dialog"
        style={{ position: 'fixed', inset: 0, zIndex: 10010, backgroundColor: 'rgba(0,0,0,0.45)' }}
        onClick={() => {
          setShowDiscardPortal(false);
          sheetControls.start({
            y: 0,
            transition: { type: 'spring', stiffness: 400, damping: 30 },
          });
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowDiscardPortal(false);
            sheetControls.start({
              y: 0,
              transition: { type: 'spring', stiffness: 400, damping: 30 },
            });
          }
        }}
      />
      {/* Discard dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="discard-dialog-title"
        style={{
          position: 'fixed',
          zIndex: 10011,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '16px',
          padding: '24px 24px 20px',
          width: 'min(320px, 88vw)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
        }}
      >
        <p
          id="discard-dialog-title"
          style={{ fontWeight: 700, fontSize: '17px', margin: '0 0 6px' }}
        >
          Discard this event?
        </p>
        <p style={{ fontSize: '14px', color: 'var(--color-content-muted)', margin: '0 0 24px' }}>
          Your changes won&apos;t be saved.
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            onClick={() => {
              setShowDiscardPortal(false);
              sheetControls.start({
                y: 0,
                transition: { type: 'spring', stiffness: 400, damping: 30 },
              });
            }}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              border: '1.5px solid var(--color-border)',
              background: 'var(--color-surface)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Keep editing
          </button>
          <button
            type="button"
            onClick={() => {
              setShowDiscardPortal(false);
              if (onDiscard) onDiscard();
              else onClose();
            }}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              border: 'none',
              background: 'var(--color-error)',
              color: 'var(--color-content-inverse)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Discard
          </button>
        </div>
      </div>
    </>
  ) : null;

  const customRecurrencePortal = showCustomRecurrence ? (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label="close dialog"
        style={{ position: 'fixed', inset: 0, zIndex: 10020, backgroundColor: 'rgba(0,0,0,0.4)' }}
        onClick={() => setShowCustomRecurrence(false)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowCustomRecurrence(false);
          }
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="recurrence-dialog-title"
        style={{
          position: 'fixed',
          zIndex: 10021,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '16px',
          padding: '24px 20px 20px',
          width: 'min(360px, 90vw)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
        }}
      >
        <p
          id="recurrence-dialog-title"
          style={{
            fontWeight: 700,
            fontSize: '17px',
            margin: '0 0 20px',
            color: 'var(--color-content)',
          }}
        >
          Custom recurrence
        </p>

        {/* Repeat every [N] [unit] */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <span style={{ fontSize: '14px', color: 'var(--color-content-muted)', flexShrink: 0 }}>
            Repeat every
          </span>
          <input
            type="number"
            min={1}
            max={99}
            value={customRepeatEvery}
            onChange={e => setCustomRepeatEvery(Math.max(1, parseInt(e.target.value, 10) || 1))}
            style={{ ...INPUT_STYLE, width: '56px', padding: '8px 10px', textAlign: 'center' }}
          />
          <select
            value={customRepeatUnit}
            onChange={e => setCustomRepeatUnit(e.target.value as 'day' | 'week' | 'month' | 'year')}
            style={{ ...INPUT_STYLE, flex: 1, padding: '8px 10px', cursor: 'pointer' }}
          >
            <option value="day">{customRepeatEvery === 1 ? 'day' : 'days'}</option>
            <option value="week">{customRepeatEvery === 1 ? 'week' : 'weeks'}</option>
            <option value="month">{customRepeatEvery === 1 ? 'month' : 'months'}</option>
            <option value="year">{customRepeatEvery === 1 ? 'year' : 'years'}</option>
          </select>
        </div>

        {/* Repeat on (days) — only for week unit */}
        {customRepeatUnit === 'week' && (
          <div style={{ marginBottom: '20px' }}>
            <p
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-content-muted)',
                marginBottom: '10px',
              }}
            >
              Repeat on
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {DAY_ABBREVS.map(([label, day]) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleCustomDay(day)}
                  data-testid={`custom-day-${day}`}
                  style={{
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    border: customRepeatDays.includes(day)
                      ? '2px solid var(--color-primary)'
                      : '1.5px solid var(--color-border)',
                    backgroundColor: customRepeatDays.includes(day)
                      ? 'rgba(65, 100, 81,0.12)'
                      : 'var(--color-surface)',
                    color: customRepeatDays.includes(day)
                      ? 'var(--color-primary)'
                      : 'var(--color-content-muted)',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: customRepeatDays.includes(day) ? 600 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Ends */}
        <div style={{ marginBottom: '24px' }}>
          <p
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--color-content-muted)',
              marginBottom: '12px',
            }}
          >
            Ends
          </p>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '12px',
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              name="ends"
              checked={customEndsType === 'never'}
              onChange={() => setCustomEndsType('never')}
            />
            <span style={{ fontSize: '14px', color: 'var(--color-content)' }}>Never</span>
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              flexWrap: 'wrap',
            }}
          >
            <input
              type="radio"
              name="ends"
              checked={customEndsType === 'on'}
              onChange={() => setCustomEndsType('on')}
            />
            <span style={{ fontSize: '14px', color: 'var(--color-content)', flexShrink: 0 }}>
              On
            </span>
            {customEndsType === 'on' && (
              <input
                type="date"
                value={customEndsDate}
                onChange={e => setCustomEndsDate(e.target.value)}
                style={{ ...INPUT_STYLE, flex: 1, padding: '6px 10px', fontSize: '14px' }}
              />
            )}
          </label>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => setShowCustomRecurrence(false)}
            style={{
              padding: '10px 20px',
              borderRadius: '10px',
              border: '1.5px solid var(--color-border)',
              background: 'var(--color-surface)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              color: 'var(--color-content-muted)',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCustomRecurrenceDone}
            style={{ ...BTN_PRIMARY, padding: '10px 20px' }}
          >
            Done
          </button>
        </div>
      </div>
    </>
  ) : null;

  return createPortal(
    <>
      {modal}
      {discardPortal}
      {customRecurrencePortal}
    </>,
    document.body
  );
};

export default EventSubmitModal;
